import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  model,
  ModelSignal,
  effect,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";

import { SymbolFormDirective } from "@bitwarden/web-vault/app/gloss/manage/manage-symbols/symbol-form/symbol-form.directive";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { HelperNumericInput } from "@bitwarden/web-vault/app/shared/utils/helper-numeric-input";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import {
  IReferenceData,
  ReferenceDataSource,
  ReferenceDataView,
  toReferenceDataView,
} from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { getSourceFromConfig } from "@bitwarden/web-vault/app/services/store/data-plugin/external.data-bank.store.collection";
import {
  clone,
  defaultGroupForCode,
  type ISymbolView,
} from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import {
  isCurrency,
  isReadOnlySymbol,
} from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import {
  SymbolGroup,
  symbolGroupDropdownOptions,
} from "@bitwarden/web-vault/app/models/types/symbol-group.types";
import { DropdownSelectMapOption } from "@bitwarden/web-vault/app/directives/dropdown.directive";

const FIND_PRICE = "findPrice";
const MANUALLY_ENTERED_PRICE = "manuallyEnterPrice";

@Component({
  selector: "app-edit-symbol-form",
  templateUrl: "./edit-symbol-form.component.html",
})
export class EditSymbolFormComponent extends SymbolFormDirective implements OnInit, OnChanges {
  symbol: ModelSignal<ISymbolView | undefined> = model<ISymbolView>(null);
  @Output() symbolUpdated = new EventEmitter<{ oldCode: string; newCode: string }>();

  @Input() formMode: "details" | "addPrice" = "addPrice";

  nameValue: string = "";
  descriptionValue: string = "";
  groupValue: SymbolGroup | null = null;

  protected isReadOnlySymbol = computed(() =>
    isReadOnlySymbol(this.symbol()?.code, this.symbol()?.sources),
  );

  private transactionStore = inject(TransactionStoreService);
  private confirmationService = inject(ConfirmationDialogService);
  private i18nService = inject(I18nService);
  formBuilder: FormBuilder = inject(FormBuilder);
  saveNameForm: FormGroup;
  addPriceFormGroup: FormGroup;

  protected groupOptions: DropdownSelectMapOption[] = symbolGroupDropdownOptions(this.i18nService);

  // CHANGED TO PUBLIC: Exposed inherited getter states to parent templates
  override isFindPrice = computed(
    () => this.currentTab() === FIND_PRICE && this.isEodhdInstalled(),
  );
  getIsFreeSubscription = computed(() => this.isFreeSubscription());

  // Computed signal for the current currency to display
  protected currentDisplayCurrency = computed(() => {
    const symbol = this.symbol();
    return symbol?.c_asOf?.symbol || this.defaultSystemCurrency;
  });

  // Check if symbol is synced with EODHD
  protected isEodhdSynced = computed(() => {
    const symbol = this.symbol();
    return !!symbol?.sources?.eodhd;
  });

  protected isAutoGeneratedCurrency = computed(() => {
    const symbol = this.symbol();
    if (!symbol) {
      return false;
    }
    return isCurrency(symbol.code) && !!symbol.sources?.[ReferenceDataSource.FOREX];
  });

  // Get the EODHD symbol code (e.g., "AAPL.US")
  protected eodhdSymbolCode = computed(() => {
    const symbol = this.symbol();
    return symbol?.sources?.eodhd || "";
  });

  // Override menu to set default to Find Price when EODHD synced and installed.
  protected editMenu = computed(() => {
    const isEodhd = this.isEodhdSynced() && this.isEodhdInstalled();
    const currentMenu = this.menu();
    const isAutoGenCurrency = this.isAutoGeneratedCurrency();

    const options = isAutoGenCurrency
      ? currentMenu.options.filter((o) => o.value !== FIND_PRICE)
      : currentMenu.options;

    return {
      ...currentMenu,
      options,
      defaultSelected: isEodhd && !isAutoGenCurrency ? FIND_PRICE : currentMenu.defaultSelected,
    };
  });

  // Glide effect for the Find Price / Manually Enter Price segmented toggle.
  protected activeTabIndex = computed(() =>
    this.editMenu().options.findIndex((o) => o.value === this.currentTab()),
  );

  constructor() {
    super();
    // Auto-update currency/price when external searchedSymbol (by date) changes
    effect(() => {
      const found = this.searchedSymbol();
      const enabled = this.enableFindPrice();
      if (!enabled || !found || !this.addPriceFormGroup) {
        return;
      }
      const currencyCtrl = this.addPriceFormGroup.controls.currency;
      const priceCtrl = this.addPriceFormGroup.controls.price;
      if (currencyCtrl.value !== found.symbol) {
        currencyCtrl.setValue(found.symbol, { emitEvent: false });
      }
      if (priceCtrl.value !== found.rate) {
        priceCtrl.setValue(found.rate, { emitEvent: false });
      }
    });
  }

  ngOnInit() {
    super.ngOnInit();

    // Read from the store to avoid stale signal values (e.g. after a previous save)
    const symbolCode = this.symbol()?.code;
    const storedSymbol = symbolCode
      ? this.transactionStore.symbols.loadedCollection()?.find((s) => s.code === symbolCode)
      : null;
    const currentSymbol = storedSymbol || this.symbol();

    // Initialize details-mode fields
    this.nameValue = currentSymbol?.code || "";
    this.descriptionValue = currentSymbol?.name || "";
    this.groupValue = currentSymbol?.group ?? defaultGroupForCode(currentSymbol?.code ?? "");

    // Set initial tab based on whether symbol is EODHD synced
    if (this.isEodhdSynced() && this.isEodhdInstalled() && !this.isAutoGeneratedCurrency()) {
      this.currentTab.set(FIND_PRICE);
      this.symbolInput.set(this.eodhdSymbolCode());
    } else {
      this.currentTab.set(MANUALLY_ENTERED_PRICE);
    }

    this.saveNameForm = this.formBuilder.group({
      name: [currentSymbol?.code, Validators.required],
    });

    this.addPriceFormGroup = this.formBuilder.group({
      // Match what app-currencies displays: its dropdown doesn't emit on init for a pre-seeded
      // value, so the form must start with the symbol's own currency, not the core currency.
      currency: [this.currentDisplayCurrency(), [Validators.required, Validators.pattern(/\S/)]],
      price: [
        this.getPriceValue() || null,
        [HelperNumericInput.isNumberValidator, Validators.required],
      ],
      date: [new Date().toISOString().split("T")[0]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.symbol && this.addPriceFormGroup) {
      // Use computed symbol from store which has fully loaded reference data
      const currentSymbol = changes.symbol.currentValue;

      // Find the symbol from the generated collection (which has merged data)
      const fullSymbol = this.transactionStore.generatedSymbols
        .collection()
        ?.find((s) => s.code === currentSymbol?.code);

      // Use the full symbol for getting price data
      const symbolToUse = fullSymbol || currentSymbol;

      const priceValue = symbolToUse?.c_asOf?.rate || null;
      this.addPriceFormGroup.controls.price.setValue(priceValue);

      // Update currency from the selected symbol's latest base rate
      if (symbolToUse?.c_asOf?.symbol) {
        this.addPriceFormGroup.controls.currency.setValue(symbolToUse.c_asOf.symbol);
      }

      if (
        symbolToUse?.sources?.eodhd &&
        this.isEodhdInstalled() &&
        !this.isAutoGeneratedCurrency()
      ) {
        this.currentTab.set(FIND_PRICE);
        this.symbolInput.set(symbolToUse.sources.eodhd);
      } else {
        this.currentTab.set(MANUALLY_ENTERED_PRICE);
      }

      this.saveNameForm?.controls.name?.setValue(symbolToUse?.name || symbolToUse?.code);
      this.groupValue = symbolToUse?.group ?? defaultGroupForCode(symbolToUse?.code ?? "");
    }
  }

  async saveName() {
    const currentDisplayName = this.symbol()?.name || this.symbol()?.code;
    if (currentDisplayName === this.saveNameForm.get("name").value?.trim()) {
      return false;
    }

    if (!this.saveNameForm.valid) {
      return;
    }

    const symbolExistingName = this.symbol().code;
    await this.updateSymbol(symbolExistingName);
  }

  private async updateSymbol(code: string) {
    try {
      // First check if this is a vault symbol (user-created/saved symbol)
      const loadedSymbols = this.transactionStore.symbols.loadedCollection();
      const vaultSymbol = loadedSymbols?.find((s) => s.code === code);

      if (vaultSymbol) {
        // This is a vault symbol - use it directly
        const sourceCode = vaultSymbol.sources[ReferenceDataSource.MANUAL];

        // new name
        const symbolNewName = this.saveNameForm.get("name").value.trim();

        // Check if the new name conflicts with ANY symbol (case-insensitive)
        // Check both vault and generated symbols
        const lowerNew = symbolNewName.toLowerCase();
        const vaultMatches = (s: { code: string; name?: string }) =>
          s.code !== code &&
          (s.code.toLowerCase() === lowerNew || s.name?.toLowerCase() === lowerNew);
        const generatedMatches = (s: { code: string }) =>
          s.code !== code && s.code.toLowerCase() === lowerNew;

        const existingVaultSymbol = loadedSymbols?.find(vaultMatches);
        const existingGeneratedSymbol = this.transactionStore.generatedSymbols
          .collection()
          ?.find(generatedMatches);

        if (existingVaultSymbol || existingGeneratedSymbol) {
          const conflictSymbol = existingVaultSymbol ?? existingGeneratedSymbol;
          const conflictDisplay = conflictSymbol.name || conflictSymbol.code;
          this.confirmationService.notify(
            "error",
            "Duplicate Symbol Name",
            `The symbol name "${symbolNewName}" conflicts with an existing symbol "${conflictDisplay}". Symbol names are case-insensitive and must be unique.`,
            false,
          );
          return;
        }

        const isEodhd = !!vaultSymbol.sources?.eodhd;
        const editedSymbol = clone(vaultSymbol);
        editedSymbol.name = symbolNewName;

        if (!isEodhd) {
          editedSymbol.code = symbolNewName;
          editedSymbol.sources[ReferenceDataSource.MANUAL] = symbolNewName;
        }

        // Rename reference rows before deleting the old symbol so a half-failed resetRefData
        // leaves the symbol under its old code instead of orphaned under the new one.
        if (!isEodhd && sourceCode) {
          await this.resetRefData(code, symbolNewName);
        }

        await this.transactionStore.symbols.delete(vaultSymbol as any);

        // Clear vaultId to create a new vault entry instead of trying to update a deleted one
        (editedSymbol as any).vaultId = null;
        await this.transactionStore.symbols.save(editedSymbol);

        // Emit event to notify parent components that symbol was updated
        this.symbolUpdated.emit({ oldCode: code, newCode: isEodhd ? code : symbolNewName });

        // Show success message
        this.confirmationService.notify("success", "success", "Symbol updated successfully");
        return;
      }

      // Not a vault symbol - check generated symbols (for backwards compatibility)
      const symbol = this.transactionStore.generatedSymbols
        .collection()
        ?.find((s) => s.code === code);

      if (!symbol) {
        this.confirmationService.notify(
          "error",
          "error",
          `Symbol "${code}" not found. It may have been deleted or is not yet saved.`,
        );
        return;
      }

      // This is a generated symbol (from reference data only, not explicitly saved)
      this.confirmationService.notify(
        "warning",
        "Cannot Edit",
        "This symbol exists only in memory (generated from reference data). It cannot be renamed. You can delete it and create a new one with your desired name.",
        false,
      );
    } catch (error) {
      this.confirmationService.notify(
        "error",
        "Update Failed",
        `Failed to update symbol: ${error.message || "Unknown error"}`,
        false,
      );
    }
  }

  private async resetRefData(symbolExistingName: string, symbolNewName: string) {
    const allRefData = this.transactionStore.referenceData.referenceDataViews();

    const referencesOnExistingName = allRefData.filter(
      (rd) => rd.base === symbolExistingName || rd.symbol === symbolExistingName,
    );

    const references = referencesOnExistingName.map((rd) => {
      const rdClone = rd.clone();
      rdClone.symbol = rd.symbol === symbolExistingName ? symbolNewName : rd.symbol;
      rdClone.base = rd.base === symbolExistingName ? symbolNewName : rd.base;
      return rdClone;
    });

    if (references.length > 0) {
      await this.transactionStore.referenceData.deleteFromVault(references);

      const newRefs = references.map((rd) => {
        const rdStore = rd.toStoreModel();
        // toStoreModel() builds the id from the renamed base/symbol, so the id stays in sync.
        rdStore.vid = null; // Clear vault ID so it's treated as new

        return new ReferenceDataView(rdStore);
      });

      await this.transactionStore.referenceData.saveToVault(newRefs, true);
    }
  }

  async addPrice() {
    let source: string = ReferenceDataSource.MANUAL;
    let base = this.symbol().code;
    const symbol = this.transactionStore.symbols.collection()?.find((s) => s.code === base);

    if (this.enableFindPrice() && this.searchedSymbol()) {
      base = this.symbolInput();
      source = getSourceFromConfig(this.dataPluginStoreService.databank.config());
      this.addPriceFormGroup.controls.currency.setValue(this.searchedSymbol().symbol);
      this.addPriceFormGroup.controls.price.setValue(this.searchedSymbol().rate);
    }

    if (!this.addPriceFormGroup.valid) {
      return;
    }

    const refData: IReferenceData = {
      date: {
        date: this.addPriceFormGroup.get("date").value,
        time: null,
        timeZone: null,
        reliableTimeSource: true,
      },
      rate: this.addPriceFormGroup.get("price").value,
      symbol: this.addPriceFormGroup.get("currency").value,
      base: base,
      source: source,
    };
    await this.saveReferenceData(refData);

    if (!symbol.sources[source]) {
      const editedSymbol = clone(symbol);
      editedSymbol.sources[source] = base;
      await this.transactionStore.symbols.save(editedSymbol);
    }
  }

  async saveReferenceData(refData: IReferenceData) {
    const referenceData = toReferenceDataView(refData);
    await this.transactionStore.referenceData.save(referenceData);
  }

  // Restrict the date field to today or earlier.
  protected dateFilter = (d: Date | null): boolean => !!d && d <= new Date();

  onDatePicked(date: Date) {
    this.onDateChange(this.datePipe.transform(date, "yyyy-MM-dd")!);
  }

  onDateChange(date: string) {
    this.addPriceFormGroup.controls.date.setValue(date);
    this.addPriceFormGroup.controls.date.markAsTouched();

    if (this.enableFindPrice()) {
      const symbolToSearch = this.isEodhdSynced() ? this.eodhdSymbolCode() : this.symbolInput();

      if (symbolToSearch) {
        this.onSearchInputChange(symbolToSearch, date);
      }
    }
  }

  onInputName(value: string, controlName: string) {
    this.saveNameForm.get(controlName)?.setValue(value);
  }

  onInput(value: string, controlName: string) {
    this.addPriceFormGroup.get(controlName)?.setValue(value);
  }

  protected getPriceValue(): number | string {
    if (this.isFindPrice()) {
      return this.searchedSymbol()?.rate || "";
    }
    return this.symbol()?.c_asOf?.rate || "";
  }

  onCodeChange(value: string): void {
    this.nameValue = value;
    this.saveNameForm?.get("name")?.setValue(value);
  }

  onDescriptionChange(value: string): void {
    this.descriptionValue = value;
  }

  onGroupChange(value: string): void {
    this.groupValue = value as SymbolGroup;
  }

  async saveDetails(): Promise<void> {
    const currentCode = this.symbol()?.code;
    const storedSymbol = currentCode
      ? this.transactionStore.symbols.loadedCollection()?.find((s) => s.code === currentCode)
      : null;
    const originalDescription = storedSymbol?.name || this.symbol()?.name || "";
    const originalGroup = storedSymbol?.group ?? defaultGroupForCode(currentCode ?? "");
    const descChanged = this.descriptionValue !== originalDescription;
    const groupChanged = this.groupValue !== originalGroup;
    const codeChanged = this.nameValue !== currentCode;

    if (descChanged) {
      await this.saveDescription();
    }

    // Group must be saved before a code rename, since renaming deletes and re-creates the
    // vault record under the new code (see updateSymbol()) - saving after would silently no-op.
    if (groupChanged) {
      await this.saveGroup();
    }

    if (codeChanged) {
      await this.saveName();
    }
  }

  private async saveDescription(): Promise<void> {
    const symbol = this.symbol();
    if (!symbol) {
      return;
    }

    const loadedSymbols = this.transactionStore.symbols.loadedCollection();
    const vaultSymbol = loadedSymbols?.find((s) => s.code === symbol.code);
    if (!vaultSymbol) {
      return;
    }

    const editedSymbol = clone(vaultSymbol);
    editedSymbol.name = this.descriptionValue;
    await this.transactionStore.symbols.save(editedSymbol);
  }

  private async saveGroup(): Promise<void> {
    const symbol = this.symbol();
    if (!symbol) {
      return;
    }

    const loadedSymbols = this.transactionStore.symbols.loadedCollection();
    const vaultSymbol = loadedSymbols?.find((s) => s.code === symbol.code);
    if (!vaultSymbol) {
      return;
    }

    const editedSymbol = clone(vaultSymbol);
    editedSymbol.group = this.groupValue;
    await this.transactionStore.symbols.save(editedSymbol);
  }
}
